<template>
    <div class="episode-modal-publication-timestamp">
        <i :class="['status-icon', 'far', icon]" />
        {{ label }}
        <div
            class="timestamp-container"
            :class="{ clickable: canBeRescheduled }"
        >
            <span class="timestamp" @click="showScheduleDatePicker">
                <span :class="{ emphasized: 'timestamp-emphasis' }">
                    {{ date }}
                </span>

                <template v-if="time">
                    {{ 'list' === context ? '@' : 'at' }}
                    <span :class="{ emphasized: 'timestamp-emphasis' }">
                        {{ time }}
                    </span>
                </template>
            </span>

            <date-time-picker
                v-if="isScheduleDatePickerVisible"
                v-on-clickaway="hideScheduleDatePicker"
                :min-date="new Date()"
                :value="episodeModal.getEpisodeData('publishedAt')"
                :disabled="episodeModal.isSaving()"
                @cancel="hideScheduleDatePicker"
                @change="scheduleEpisodePublication"
            />
        </div>
    </div>
</template>
<script>
import moment from 'moment-timezone';
import { mixin as clickaway } from 'vue-clickaway';
import DateTimePicker from '@/components/Controls/DateTimePicker';
import { publicationDateToEpisodeStatus } from '@/utils/config/episodeStatuses';
import episodeModal from '@/utils/episodeModal';

export default {
    components: {
        DateTimePicker,
    },

    mixins: [
        clickaway,
    ],

    props: {
        context: {
            required: true,
            type: String,
        },
        emphasized: {
            type: Boolean,
            default: true,
        },
        publishedAt: {
            type: String,
            default: null,
        },
        createdAt: {
            type: String,
            default: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        },
    },

    data: () => ({
        episodeModal,
        isScheduleDatePickerVisible: false,
    }),

    computed: {
        /**
         * Returns Episode publication timestamp.
         *
         * @returns {moment|null} Publication timestamp.
         */
        publicationTimestamp() {
            if (this.publishedAt) {
                return moment.utc(this.publishedAt).tz(moment.tz.guess());
            }

            return null;
        },

        /**
         * Returns Episode status based on publication date.
         *
         * @returns {string} Episode status.
         */
        status() {
            return publicationDateToEpisodeStatus(this.publishedAt);
        },

        /**
         * Returns Episode creation timestamp.
         *
         * @returns {moment} Creation timestamp.
         */
        creationTimestamp() {
            return moment.utc(this.createdAt).tz(moment.tz.guess());
        },

        /**
         * Returns Episode timestamp based on status.
         *
         * @returns {moment} Episode timestamp.
         */
        timestamp() {
            if (! this.isUnpublished) {
                return this.publicationTimestamp;
            }

            return this.publicationTimestamp || this.creationTimestamp;
        },

        /**
         * Indicates whether Episode is published or not.
         *
         * @returns {boolean} Episode is published or not?
         */
        isPublished() {
            return 'published' === this.status &&
                ['edit', 'list'].includes(this.context);
        },

        /**
         * Indicates whether Episode is scheduled or not.
         *
         * @returns {boolean} Episode is scheduled or not?
         */
        isScheduled() {
            return 'scheduled' === this.status;
        },

        /**
         * Indicates whether Episode is unpublished or not.
         *
         * @returns {boolean} Episode is unpublished or not?
         */
        isUnpublished() {
            return ! this.isPublished && ! this.isScheduled;
        },

        /**
         * Returns formatted date.
         *
         * @returns {string} Formatted date.
         */
        date() {
            return this.timestamp.format(
                'list' === this.context ? 'MMM Do, YYYY' : 'MMMM Do, YYYY'
            );
        },

        /**
         * Returns formatted time.
         *
         * @returns {string|null} Formatted time.
         */
        time() {
            if (this.isUnpublished || (this.isPublished && 'list' === this.context)) {
                return null;
            }

            return this.timestamp.format('h:mma z');
        },

        /**
         * Returns icon indicating Episode status.
         *
         * @returns {object} Episode icon.
         */
        icon() {
            return {
                'status-icon-published fa-check-circle': this.isPublished,
                'status-icon-unpublished fa-pen': this.isUnpublished,
                'status-icon-scheduled fa-calendar-alt': this.isScheduled,
            };
        },

        /**
         * Returns timestamp label.
         *
         * @returns {string} Timestamp label.
         */
        label() {
            return this.isScheduled
                ? 'Scheduled on'
                : this.isPublished
                    ? 'Published on'
                    : 'Saved as draft on';
        },

        /**
         * Checks whether episode can be rescheduled.
         *
         * @returns {boolean} Check result.
         */
        canBeRescheduled() {
            return episodeModal.inEditContext() && this.isScheduled;
        },
    },

    methods: {
        /**
         * Shows schedule date picker.
         *
         * @returns {void}
         */
        showScheduleDatePicker() {
            if (! this.canBeRescheduled) {
                return;
            }

            this.isScheduleDatePickerVisible = true;
        },

        /**
         * Hides schedule date picker.
         */
        hideScheduleDatePicker() {
            this.isScheduleDatePickerVisible = false;
        },

        /**
         * Schedules episode publication.
         *
         * @param {string} value Schedule date.
         */
        scheduleEpisodePublication(value) {
            if (null !== value) {
                this.$emit('reschedule', value);
            }
        },
    },
};
</script>
